import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import { Button } from '@common/bw-ui/ui/button';
import { Input } from '@common/bw-ui/ui/input';

export interface DiscountCodeInputProps {
  onApply: (code: string) => Promise<void>;
  appliedCode?: string;
  error?: string;
  className?: string;
}

function getAriaDescribedBy(error?: string, appliedCode?: string) {
  if (error) return 'discount-error';
  if (appliedCode) return 'discount-applied';
  return undefined;
}

export function DiscountCodeInput({
  onApply,
  appliedCode,
  error: externalError,
  className,
}: DiscountCodeInputProps) {
  const [code, setCode] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [internalError, setInternalError] = React.useState<string>();

  const error = externalError || internalError;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    setInternalError(undefined);
    setIsLoading(true);
    try {
      await onApply(trimmed);
    } catch (err: unknown) {
      setInternalError(
        (err instanceof Error && err.message) || 'Invalid discount code',
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('tw:flex tw:flex-col tw:gap-2', className)}
    >
      <label
        data-slot="label"
        htmlFor="discount-code"
        className="tw:text-sm tw:text-foreground"
      >
        Discount code
      </label>
      <div data-slot="input-group" className="tw:flex tw:gap-2">
        <Input
          id="discount-code"
          type="text"
          placeholder="Enter discount code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={isLoading}
          className="tw:bg-surface-muted tw:border-border"
          aria-describedby={getAriaDescribedBy(error, appliedCode)}
        />
        <Button
          type="submit"
          variant="outline"
          disabled={isLoading || !code.trim()}
          className="tw:shrink-0"
        >
          {isLoading ? 'Applying…' : 'Apply'}
        </Button>
      </div>
      {error && (
        <p
          id="discount-error"
          data-slot="error"
          className="tw:text-sm tw:text-destructive"
          role="alert"
        >
          {error}
        </p>
      )}
      {appliedCode && !error && (
        <p
          id="discount-applied"
          data-slot="applied"
          className="tw:text-sm tw:text-success"
        >
          {appliedCode} applied.
        </p>
      )}
    </form>
  );
}
